import React, { useState } from "react";
import QRCodeRenderer from "./QRCodeRenderer";
import { useNotification } from "../../context/NotificationContext";
import "../../styles/LogoUploadModal.css";
import Button from "./Button";
import { resizeImageIfNeeded } from "../utils/imageUtils";

interface LogoUploadModalProps {
  onClose: () => void;
  onUpload: (file: File, name: string) => Promise<boolean>;
  validateFile: (file: File) => string | null;
  validateName: (name: string) => string | null;
}

const LogoUploadModal: React.FC<LogoUploadModalProps> = ({
  onClose,
  onUpload,
  validateFile,
  validateName,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>("");
  const [logoName, setLogoName] = useState<string>("");
  const [isDragging, setIsDragging] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { setNotification } = useNotification();

  const handleFileSelect = async (file: File) => {
    const error = validateFile(file);
    if (error) {
      setNotification({ type: "error", message: error });
      return;
    }
    try {
      const processedFile = await resizeImageIfNeeded(file);
      setSelectedFile(processedFile);
      setLogoName(file.name.replace(/\.[^/.]+$/, ""));
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewUrl(e.target?.result as string);
      };
      reader.readAsDataURL(processedFile);
    } catch (error) {
      setNotification({ type: "error", message: "Failed process." });
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files.length > 0)
      handleFileSelect(e.dataTransfer.files[0]);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleSubmit = async () => {
    if (!selectedFile || !logoName.trim()) return;
    const nameError = validateName(logoName.trim());
    if (nameError) {
      setNotification({ type: "error", message: nameError });
      return;
    }
    setIsSubmitting(true);
    try {
      await onUpload(selectedFile, logoName.trim());
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetUpload = () => {
    setSelectedFile(null);
    setPreviewUrl("");
    setLogoName("");
  };

  return (
    <div className="logo-upload-modal-overlay" onClick={onClose}>
      <div className="logo-upload-modal" onClick={(e) => e.stopPropagation()}>
        <div className="logo-modal-gradient-bar" />

        <div className="logo-modal-header">
          <h3>Upload Logo</h3>
          <button
            className="logo-modal-close-btn"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="logo-modal-content">
          <div className="logo-upload-section">
            {!selectedFile ? (
              <div
                className={`logo-file-upload-area ${
                  isDragging ? "logo-dragging" : ""
                }`}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onClick={() =>
                  document.getElementById("logo-file-input")?.click()
                }
              >
                <div className="logo-upload-icon-wrapper">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M12 15V3M12 3L8 7M12 3L16 7"
                      stroke="#5236AB"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M2 17V19C2 20.1046 2.89543 21 4 21H20C21.1046 21 22 20.1046 22 19V17"
                      stroke="#5236AB"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <p className="logo-upload-primary-text">
                  Drag your file here, or select from your device
                </p>
                <p className="logo-upload-secondary-text">
                  PNG, JPG • Max 35KB
                </p>
                <input
                  type="file"
                  id="logo-file-input"
                  accept=".png,.jpg,.jpeg"
                  style={{ display: "none" }}
                  onChange={(e) =>
                    e.target.files?.[0] && handleFileSelect(e.target.files[0])
                  }
                />
              </div>
            ) : (
              <div className="logo-file-list">
                <div className="logo-file-item">
                  <div className="logo-file-icon">
                    <img src={previewUrl} alt="Logo preview" />
                  </div>
                  <div className="logo-file-details">
                    <div className="logo-file-name">{selectedFile.name}</div>
                    <div className="logo-file-size">
                      {(selectedFile.size / 1024).toFixed(1)} KB
                    </div>
                  </div>
                  <button
                    type="button"
                    className="logo-file-remove"
                    onClick={resetUpload}
                    disabled={isSubmitting}
                  >
                    ×
                  </button>
                </div>
              </div>
            )}
          </div>

          {selectedFile && (
            <div className="logo-preview-section">
              <h4>Preview on QR Code</h4>
              <div className="logo-qr-preview-container">
                <div
                  className="logo-qr-code-sample"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <QRCodeRenderer
                    value="https://example.com/sample"
                    size={160}
                    color="#000000"
                    withLogo={true}
                    logoUrl={previewUrl}
                  />
                </div>
              </div>
              <p className="logo-preview-description">
                Your logo will appear in the center of QR codes
              </p>
            </div>
          )}
        </div>

        <div className="logo-modal-footer">
          <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={!selectedFile || !logoName.trim() || isSubmitting}
          >
            {isSubmitting ? "Uploading..." : "Upload"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default LogoUploadModal;
